import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import tripService from '@/services/tripService';
import { CommonButton } from '@/components/ui/common-button';
import { CommonLabel } from '@/components/ui/common-label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

export function UnclosedTripsNotifier() {
  const navigate = useNavigate();
  const { userRole, userProfile } = useAuth();
  const [alertMessage, setAlertMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dismissedAlert, setDismissedAlert] = useState(null);
  const dismissedAlertRef = useRef(null);

  const rawRole = userProfile?.role || userRole;
  const effectiveRole = rawRole?.toLowerCase();

  useEffect(() => {
    if (effectiveRole !== 'port_admin') {
      setAlertMessage('');
      setIsModalOpen(false);
      return;
    }

    const fetchUnclosedTrips = async () => {
      try {
        const response = await tripService.getUnclosedTrips(0, 100);
        if (response) {
          if (response.show_alert && response.alert_message) {
            setAlertMessage(response.alert_message);
            
            if (response.alert_message !== dismissedAlertRef.current) {
              setIsModalOpen(true);
            }
          } else {
            setAlertMessage('');
            setIsModalOpen(false);
          }
        }
      } catch (error) {
        console.error('Error fetching unclosed trips:', error);
      }
    };

    fetchUnclosedTrips(); 

    const intervalId = setInterval(fetchUnclosedTrips, 30000); 

    return () => clearInterval(intervalId);
  }, [effectiveRole]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    dismissedAlertRef.current = alertMessage;
    setDismissedAlert(alertMessage);
  };

  const handleViewTrips = () => {
    setIsModalOpen(false);
    dismissedAlertRef.current = alertMessage;
    setDismissedAlert(alertMessage);
    navigate('/trip-management');
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={(open) => {
      if (!open) {
        handleCloseModal();
      }
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Unclosed Trips Alert</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground mt-0.5">
            Port Administration Warning
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <CommonLabel size="sm" weight="medium">
            {alertMessage}
          </CommonLabel>
        </div>
        <DialogFooter className="flex justify-end space-x-2">
          <CommonButton
            variant="outline"
            onClick={handleCloseModal}
          >
            Dismiss
          </CommonButton>
          <CommonButton
            variant="default"
            onClick={handleViewTrips}
          >
            View Trips
          </CommonButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UnclosedTripsNotifier;
